import { useKeycloak } from '@State/Keycloak/KeycloakProvider';
import { t } from 'i18next';
import type Keycloak from 'keycloak-js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSnackbar from '../Snackbar/useSnackbar';
import { AuthContext, AuthContextProps } from './AuthContext';
import { ParsedToken } from './AuthModel';
import { clearStoredSession } from './session';

/** Så många sekunder ett token minst ska vara giltigt innan det används */
const TOKEN_MIN_VALIDITY_S = 30;

const mapAuthState = (keycloak: Keycloak) => ({
  token: keycloak.token,
  parsedToken: keycloak.tokenParsed as ParsedToken | undefined,
});

interface AuthState {
  token?: string;
  parsedToken?: ParsedToken;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const { keycloak } = useKeycloak();
  const { pushSnackbar } = useSnackbar();
  const [auth, setAuth] = useState<AuthState>(mapAuthState(keycloak));
  // Sessionen avslutas en gång; nollas när ett nytt token kommer in
  const sessionEnded = useRef(false);

  const onAuthRefreshSuccess = async () => {
    if (keycloak.token) {
      sessionEnded.current = false;
      setAuth(mapAuthState(keycloak));

      sessionStorage.setItem('kc_token', keycloak.token);
    }
    if (keycloak.refreshToken) {
      sessionStorage.setItem('kc_refreshToken', keycloak.refreshToken);
    }
  };

  /**
   * Går inte token att förnya är sessionen slut. Tidigare kastades ett
   * ohanterat fel och portalen stod kvar med authenticated=true men utan
   * token, så varje sida fick "Missing token". Nu rensas sessionen, Keycloak
   * släpper token (onAuthLogout) och RedirectToLogin tar över på privata
   * routes. Användaren får veta varför inloggningssidan dyker upp igen.
   *
   * Hit kommer man från flera håll: catch efter updateToken, onAuthLogout
   * när keycloak-js själv släppt token (400 på refresh, eller status-
   * iframen som säger "changed") och från flera samtidiga API-anrop. En
   * ref-flagga ser till att bara det första anropet gör något. Flaggan kan
   * inte bygga på keycloak.token: vid 400 har keycloak-js redan rensat det
   * innan vårt catch körs, så guarden slog till på exakt det fall den
   * fanns för och storage blev aldrig rensad.
   */
  const endSession = useCallback(() => {
    if (sessionEnded.current) return;
    sessionEnded.current = true;

    pushSnackbar({ type: 'warning', reactNode: <>{t('auth_session_expired')}</> });
    clearStoredSession();
    setAuth({ token: undefined, parsedToken: undefined });
    // No-op när keycloak-js redan släppt token, annars (nätverksfel) släpps
    // det här så att onAuthLogout och omrenderingen i KeycloakProvider sker
    keycloak.clearToken();
  }, [keycloak, pushSnackbar]);

  // Hakarna nedan kopplas en gång, så callbacken når senaste endSession via ref
  const endSessionRef = useRef(endSession);
  endSessionRef.current = endSession;

  const onTokenExpired = useCallback(async () => {
    try {
      await keycloak.updateToken(TOKEN_MIN_VALIDITY_S);
    } catch (e) {
      endSession();
    }
  }, [keycloak, endSession]);

  /**
   * Keycloaks rekommenderade mönster: förnya före varje skyddat anrop i
   * stället för att lita på adapterns timer. Timern stryps i bakgrundsflikar
   * och pausas när datorn sover, så ett anrop direkt efter att fliken vaknat
   * gick annars iväg med ett utgånget token och fick 401. updateToken svarar
   * direkt utan nätverksanrop när token fortfarande är giltigt, och köar
   * samtidiga anrop bakom en och samma förnyelse.
   */
  const getAccessToken = useCallback(async () => {
    if (!keycloak.authenticated || !keycloak.token) {
      throw new Error('Missing token');
    }

    try {
      await keycloak.updateToken(TOKEN_MIN_VALIDITY_S);
    } catch (e) {
      endSession();
      throw new Error('Session expired');
    }

    if (!keycloak.token) {
      throw new Error('Missing token');
    }

    return keycloak.token;
  }, [keycloak, endSession]);

  const onAuthSuccess = () => {
    if (keycloak.isTokenExpired(TOKEN_MIN_VALIDITY_S)) {
      onTokenExpired();
      return;
    }

    /* Fixes issue when redirect is performed from keycloak (signin)
     * Will prevent that the 404-page is served even if correct route
     * exist and is matching.
     */
    const oldHash = window.location.hash;
    window.location.hash = `${oldHash}/`;
    window.location.hash = oldHash;
    onAuthRefreshSuccess();
  };

  useEffect(() => {
    keycloak.onAuthRefreshSuccess = onAuthRefreshSuccess;
    keycloak.onAuthSuccess = onAuthSuccess;
    keycloak.onTokenExpired = onTokenExpired;

    // KeycloakProvider renderar om på onAuthLogout; det ska bestå. Vi lägger
    // till att sessionen avslutas, oavsett om det var keycloak-js eller vi
    // som släppte token.
    const providerOnAuthLogout = keycloak.onAuthLogout;
    keycloak.onAuthLogout = () => {
      providerOnAuthLogout?.();
      endSessionRef.current();
    };
    // Adapterns callbacks kopplas en gång; onAuthSuccess är inte memoiserad
    // och ska inte kopplas om vid varje rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = useCallback(() => {
    keycloak.logout();
    clearStoredSession();
  }, [keycloak]);

  const value: AuthContextProps = useMemo(
    () => ({
      ...auth,
      authenticated: !!keycloak.authenticated,
      getAccessToken,
      signOut,
    }),
    [auth, keycloak.authenticated, getAccessToken, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
